import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FieldWrap,
    Label,
    InputShell,
    Input,
    Affix,
    IconAction,
    HelperRow,
    Hint,
    Counter,
    ErrorText,
} from './Field.styles';
import { VisuallyHidden } from '../OnboardingLayout/OnboardingLayout.styles';
import { CLARITY_MASK } from '../../../../utils/clarity';

export const describedBy = (id, { error, hint }) =>
    [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(' ') ||
    undefined;

/** Inline error, always mounted so the aria-live region announces changes. */
export const FieldError = ({ id, error }) => {
    const { t } = useTranslation();
    return (
        <ErrorText id={`${id}-error`} aria-live="polite">
            {error ? (
                <p>
                    <i className="ri-error-warning-fill" aria-hidden="true" />
                    <span>
                        <VisuallyHidden>{t('SignUpV2.common.errorPrefix')}</VisuallyHidden>
                        {error}
                    </span>
                </p>
            ) : null}
        </ErrorText>
    );
};

/** Label + control + hint/counter + error. */
export const FieldShell = ({
    id,
    label,
    optional = false,
    hint,
    hintOk = false,
    error,
    counter,
    hiddenLabel = false,
    children,
}) => {
    const { t } = useTranslation();
    const labelContent = (
        <>
            {label}
            {optional && <small>{` ${t('SignUpV2.common.optional')}`}</small>}
        </>
    );

    return (
        <FieldWrap>
            {hiddenLabel ? (
                <VisuallyHidden as="label" htmlFor={id}>
                    {labelContent}
                </VisuallyHidden>
            ) : (
                <Label htmlFor={id}>{labelContent}</Label>
            )}
            {children}
            {(hint || counter) && (
                <HelperRow>
                    {hint ? (
                        <Hint id={`${id}-hint`} ok={hintOk}>
                            {hintOk && <i className="ri-checkbox-circle-fill" aria-hidden="true" />}
                            {hint}
                        </Hint>
                    ) : (
                        <span />
                    )}
                    {counter && <Counter aria-hidden="true">{counter}</Counter>}
                </HelperRow>
            )}
            <FieldError id={id} error={error} />
        </FieldWrap>
    );
};

/**
 * Text input (native, 16px) to be used with react-hook-form `register`.
 * `onClear` + `showClear` render a 44px clear button.
 */
export const TextField = forwardRef(
    (
        {
            id,
            label,
            optional,
            hint,
            hintOk,
            error,
            counter,
            prefix,
            suffix,
            endAdornment,
            onClear,
            showClear = false,
            ...inputProps
        },
        ref
    ) => {
        const { t } = useTranslation();
        return (
            <FieldShell
                id={id}
                label={label}
                optional={optional}
                hint={hint}
                hintOk={hintOk}
                error={error}
                counter={counter}
            >
                <InputShell invalid={!!error}>
                    {prefix && (
                        <Affix side="left" aria-hidden="true">
                            {prefix}
                        </Affix>
                    )}
                    <Input
                        id={id}
                        ref={ref}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={describedBy(id, { error, hint })}
                        {...inputProps}
                    />
                    {suffix && (
                        <Affix side="right" aria-hidden="true">
                            {suffix}
                        </Affix>
                    )}
                    {endAdornment}
                    {onClear && showClear && (
                        <IconAction
                            type="button"
                            subtle
                            onClick={onClear}
                            aria-label={t('SignUpV2.common.clear')}
                        >
                            <i className="ri-close-circle-fill" aria-hidden="true" />
                        </IconAction>
                    )}
                </InputShell>
            </FieldShell>
        );
    }
);

/** Password input with a 44px show/hide toggle (aria-pressed), masked in Clarity recordings. */
export const PasswordField = forwardRef(
    ({ id, label, hint, hintOk, error, ...inputProps }, ref) => {
        const { t } = useTranslation();
        const [visible, setVisible] = useState(false);

        return (
            <FieldShell id={id} label={label} hint={hint} hintOk={hintOk} error={error}>
                <InputShell invalid={!!error} {...CLARITY_MASK}>
                    <Input
                        id={id}
                        ref={ref}
                        type={visible ? 'text' : 'password'}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={describedBy(id, { error, hint })}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        {...inputProps}
                    />
                    <IconAction
                        type="button"
                        onClick={() => setVisible((value) => !value)}
                        aria-label={
                            visible
                                ? t('SignUpV2.common.hidePassword')
                                : t('SignUpV2.common.showPassword')
                        }
                        aria-pressed={visible}
                    >
                        <i
                            className={visible ? 'ri-eye-off-line' : 'ri-eye-line'}
                            aria-hidden="true"
                        />
                    </IconAction>
                </InputShell>
            </FieldShell>
        );
    }
);
